import { useMemo, useState } from 'react';
import {
  COMPANY_INCLUDE,
  COMPANY_TYPE,
  NOTIFICATIONDELIVERYSTATUS_UNKNOWN,
  NOTIFICATIONQUEUE_TYPE,
  USER_INCLUDE,
  USER_TYPE,
} from '../../../../../../constants/api';
import {
  getAttr,
  getEntityId,
  getRelId,
  relIsPresent,
} from '../../../../../../features/helpers/apiHelpers';
import { noSelectionKey } from '../../../../../../utilities/filterAndSortingsUtil';

function enrichInvitationSourceMappings(
  invitationSourceMappings,
  invitationSources,
  users,
  companies,
  userHearingRoles,
  companyHearingRoles,
  notifications,
  notificationQueues,
) {
  const userMap = new Map(users.map((user) => [getEntityId(user), user]));
  const companyMap = new Map(
    companies.map((company) => [getEntityId(company), company]),
  );
  const invitationSourceMap = new Map(
    invitationSources.map((invitationSource) => [
      getEntityId(invitationSource),
      invitationSource,
    ]),
  );
  const userHearingRoleMap = new Map(
    userHearingRoles.map((role) => [getEntityId(role), role]),
  );
  const companyHearingRoleMap = new Map(
    companyHearingRoles.map((role) => [getEntityId(role), role]),
  );

  const userNotificationMap = new Map(
    notifications
      .filter((notification) => relIsPresent(notification, USER_TYPE))
      .map((notification) => [getRelId(notification, USER_TYPE), notification]),
  );

  const companyNotificationMap = new Map(
    notifications
      .filter((notification) => relIsPresent(notification, COMPANY_TYPE))
      .map((notification) => [
        getRelId(notification, COMPANY_TYPE),
        notification,
      ]),
  );

  const notificationQueueMap = new Map(
    notificationQueues.map((nq) => [getEntityId(nq), nq]),
  );

  return invitationSourceMappings.map((invitationSourceMapping) => {
    const enrichedInvitationSourceMapping = {
      ...invitationSourceMapping.attributes,
      id: invitationSourceMapping.id,
      notificationStatus: NOTIFICATIONDELIVERYSTATUS_UNKNOWN,
      isSentToQueue: false,
    };

    // Find the related invitation source
    if (getAttr(invitationSourceMapping, 'invitationSourceId')) {
      const invitationSource = invitationSourceMap.get(
        getAttr(invitationSourceMapping, 'invitationSourceId').toString(),
      );

      if (invitationSource) {
        enrichedInvitationSourceMapping.canDeleteIndividuals = getAttr(
          invitationSource,
          'canDeleteIndividuals',
        );
      }
    }

    // Check if this mapping relates to a user hearing role
    if (getAttr(invitationSourceMapping, 'userHearingRoleId')) {
      const userHearingRole = userHearingRoleMap.get(
        getAttr(invitationSourceMapping, 'userHearingRoleId').toString(),
      );

      if (userHearingRole) {
        const userId = getRelId(userHearingRole, USER_INCLUDE);
        const user = userMap.get(userId);

        if (user) {
          enrichedInvitationSourceMapping.invitationKey =
            getAttr(user, 'cpr') || getAttr(user, 'email');
          enrichedInvitationSourceMapping.name = getAttr(user, 'name'); // This will not exist for users who haven't logged in

          const notification = userNotificationMap.get(userId);
          if (notification) {
            addNotificationInfo(
              enrichedInvitationSourceMapping,
              notification,
              notificationQueueMap,
            );
          }
        }
      }
    }

    // Check if this mapping relates to a company hearing role
    else if (getAttr(invitationSourceMapping, 'companyHearingRoleId')) {
      const companyHearingRole = companyHearingRoleMap.get(
        getAttr(invitationSourceMapping, 'companyHearingRoleId').toString(),
      );

      if (companyHearingRole) {
        const companyId = getRelId(companyHearingRole, COMPANY_INCLUDE);
        const company = companyMap.get(companyId);

        if (company) {
          enrichedInvitationSourceMapping.invitationKey = getAttr(
            company,
            'cvr',
          );
          enrichedInvitationSourceMapping.name = getAttr(company, 'name'); // This will not exist for users who haven't logged in

          const notification = companyNotificationMap.get(companyId);
          if (notification) {
            addNotificationInfo(
              enrichedInvitationSourceMapping,
              notification,
              notificationQueueMap,
            );
          }
        }
      }
    }

    return enrichedInvitationSourceMapping;
  });
}

function addNotificationInfo(
  invitationSourceMapping,
  notification,
  notificationQueueMap,
) {
  var isSentToQueue = getAttr(notification, 'isSentToQueue');
  invitationSourceMapping.isSentToQueue = isSentToQueue;

  const notificationQueue = notificationQueueMap.get(
    getRelId(notification, NOTIFICATIONQUEUE_TYPE),
  );

  if (notificationQueue) {
    invitationSourceMapping.notificationStatus = getAttr(
      notificationQueue,
      'deliveryStatus',
    );

    invitationSourceMapping.notificationSentAs = getAttr(
      notificationQueue,
      'sentAs',
    );

    invitationSourceMapping.deliveryDate = getAttr(
      notificationQueue,
      'successfulDeliveryDate',
    );
  }
}

function createGroupedData(invitationSourceMappings) {
  const grouped = invitationSourceMappings.reduce((groups, mapping) => {
    const sourceName = mapping.sourceName;
    const id = mapping.id;
    const canDeleteIndividuals = mapping.canDeleteIndividuals;

    if (!groups[sourceName]) {
      groups[sourceName] = {
        groupId: id,
        sourceName: sourceName,
        canDeleteIndividuals,
        invitationSourceMappings: [],
      };
    }

    groups[sourceName].invitationSourceMappings.push(mapping);

    return groups;
  }, {});

  return Object.values(grouped);
}

function transformForDetailsList(groupedData, titleFilter) {
  const flatItems = [];
  const groups = [];

  groupedData.forEach((groupData) => {
    const groupName = groupData.sourceName;
    const groupId = groupData.groupId;
    const canDeleteIndividuals = groupData.canDeleteIndividuals;
    const invitationSourcemappings = groupData.invitationSourceMappings || [];

    const group = {
      // DetailList properties
      key: `group_${groupName}`,
      startIndex: flatItems.length,
      count: invitationSourcemappings.length,
      level: 0,
      isCollapsed: !titleFilter,
      // Column properties
      name: groupName,
      // Custom data
      data: { canDeleteIndividuals, groupId, groupName },
    };

    invitationSourcemappings.forEach((invitationSourceMapping) => {
      flatItems.push({
        // DetailList properties
        key: `${groupName}_${invitationSourceMapping.id}`,
        groupId: groupName,
        groupName: groupName,
        // Column properties
        name: invitationSourceMapping.name,
        invitationKey: invitationSourceMapping.invitationKey,
        invitationStatus: invitationSourceMapping.notificationStatus,
        sentAs: invitationSourceMapping.notificationSentAs,
        // Custom data
        data: {
          id: invitationSourceMapping.id,
          notificationStatus: invitationSourceMapping.notificationStatus,
          isSentToQueue: invitationSourceMapping.isSentToQueue,
          canDeleteIndividuals,
          groupName,
        },
      });
    });

    groups.push(group);
  });

  return { items: flatItems, groups };
}

function filterItems(groups, items, filterText) {
  if (!filterText || filterText === noSelectionKey) {
    return { filteredGroups: groups, filteredItems: items };
  }

  const filteredItems = items.filter((item) =>
    item.invitationKey.toLowerCase().includes(filterText.toLowerCase()),
  );

  let currentIndex = 0;
  const updatedGroups = groups.map((group) => {
    const groupItemCount = filteredItems.filter(
      (item) => item.groupName === group.data.groupName,
    ).length;

    const updatedGroup = {
      ...group,
      startIndex: currentIndex,
      count: groupItemCount,
    };

    currentIndex += groupItemCount;
    return updatedGroup;
  });

  return {
    filteredGroups: updatedGroups,
    filteredItems: filteredItems,
  };
}

function sortGroupsAlphabetically(groups, items) {
  const sortedGroups = [...groups].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );

  const sortedItems = [];
  const updatedGroups = sortedGroups.map((group) => {
    const groupItems = items.filter(
      (item) => item.groupName === group.data.groupName,
    );
    const startIndex = sortedItems.length;
    const updatedGroup = {
      ...group,
      startIndex,
    };
    sortedItems.push(...groupItems);
    return updatedGroup;
  });

  return { sortedGroups: updatedGroups, sortedItems };
}

export const useFilterAndSort = (
  invitationSourceMappings,
  invitationSources,
  invitedUsers,
  invitedCompanies,
  invitedUserHearingRoles,
  invitedCompanyHearingRoles,
  invitationNotifications,
  invitationNotificationQueues,
) => {
  const [titleFilter, setTitleFilter] = useState(null);

  const { filteredAndSortedItems, groups } = useMemo(() => {
    if (!invitationSourceMappings) {
      return { filteredAndSortedItems: [], groups: [] };
    }
    const enrichedInvitationSourceMappings = enrichInvitationSourceMappings(
      invitationSourceMappings,
      invitationSources,
      invitedUsers,
      invitedCompanies,
      invitedUserHearingRoles,
      invitedCompanyHearingRoles,
      invitationNotifications,
      invitationNotificationQueues,
    );
    const groupedData = createGroupedData(enrichedInvitationSourceMappings);
    const detailsListData = transformForDetailsList(groupedData, titleFilter);

    const { filteredGroups, filteredItems } = filterItems(
      detailsListData.groups,
      detailsListData.items,
      titleFilter,
    );

    const { sortedGroups, sortedItems } = sortGroupsAlphabetically(
      filteredGroups,
      filteredItems,
    );

    return {
      filteredAndSortedItems: sortedItems,
      groups: sortedGroups,
    };
  }, [
    invitationSourceMappings,
    invitationSources,
    invitedCompanies,
    invitedCompanyHearingRoles,
    invitedUserHearingRoles,
    invitedUsers,
    titleFilter,
    invitationNotifications,
    invitationNotificationQueues,
  ]);

  const handleTitleFilterChange = (value) => {
    setTitleFilter(value);
  };

  return {
    titleFilter,
    filteredAndSortedItems,
    groups,
    handleTitleFilterChange,
  };
};
